/* eslint-disable react/require-default-props */
import React, { useCallback, useMemo } from 'react';
import { Modal } from '@components/v2/Modal';
import { Button } from '@ui';
import { InlineEdit } from '@components/v2/InlineEdit';
import Select from '@components/v2/Select';
import { translate } from '@utils/i18n';
import { getNumberMaxLength } from '@utils/functions/number.functions';
import CheckboxList from '@components/v2/CheckboxList/CheckboxList';
import './ComponentDetailModalStyles.scss';
const namespace = 'components.detail_modal';
// Debounce utility function
const useDebounce = (callback, delay) => {
    const timeoutRef = React.useRef();
    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => callback(...args), delay);
    }, [callback, delay]);
};
export default function ComponentDetailModal({ isOpen, onClose, component, components, componentTypes, onUpdate, onToggleActive, isTogglingActive }) {
    var _a;
    // Store original values to detect changes
    const originalValuesRef = React.useRef({});
    const [draftSettings, setDraftSettings] = React.useState({});
    // Update original values when component changes
    React.useEffect(() => {
        if (component) {
            const settings = component.settings || {};
            setDraftSettings(settings);
            originalValuesRef.current = Object.assign({ name: component.name || '', description: component.description || '', location: component.location || '', barcode: component.barcode || '', externalId: component.externalId || '', notes: component.notes || '', settings }, Object.entries(settings).reduce((acc, [key, value]) => (Object.assign(Object.assign({}, acc), { [`settings.${key}`]: value })), {}));
        }
    }, [component === null || component === void 0 ? void 0 : component.id]); // Only update when component changes
    // Flatten the full component tree for parent look-up
    const flattenAllComponents = (comps) => comps.reduce((acc, comp) => {
        acc.push(comp);
        if (comp.children)
            acc.push(...flattenAllComponents(comp.children));
        return acc;
    }, []);
    // Read-only parent display: parent component name, or attachable display name + type
    const parentDisplay = useMemo(() => {
        if (!component)
            return '--';
        if (component.parentId) {
            const allFlat = flattenAllComponents(components);
            const parentComp = allFlat.find((c) => c.id === component.parentId);
            return parentComp ? parentComp.name : `Component #${component.parentId}`;
        }
        const name = component.attachableDisplayName || '--';
        const type = component.attachableHumanReadableType || component.attachableType || '';
        return type ? `${name} (${type})` : name;
    }, [component, components]);
    // Get field definitions for component type
    const selectedComponentType = componentTypes.find((type) => type.id === (component === null || component === void 0 ? void 0 : component.typeId));
    const fieldDefinitions = ((_a = selectedComponentType === null || selectedComponentType === void 0 ? void 0 : selectedComponentType.fieldDefinitions) === null || _a === void 0 ? void 0 : _a.fields) || [];
    // Debounced update function (500ms delay for security)
    const debouncedUpdate = useDebounce((componentId, field, value) => {
        void onUpdate(componentId, field, value);
    }, 500);
    const debouncedSettingsUpdate = useDebounce((componentId, fieldKey, updatedSettings, previousSettings, previousValue) => {
        void onUpdate(componentId, 'settings', updatedSettings).catch(() => {
            setDraftSettings(previousSettings);
            originalValuesRef.current[`settings.${fieldKey}`] = previousValue;
            originalValuesRef.current.settings = previousSettings;
        });
    }, 500);
    const renderRequiredLabel = (label) => (React.createElement(React.Fragment, null,
        label,
        React.createElement("span", { className: "form-field__required" }, "*")));
    const renderFieldLabel = (fieldDef) => fieldDef.required ? renderRequiredLabel(fieldDef.label) : fieldDef.label;
    const optionLabel = (fieldDef, option, index) => { var _a; return ((_a = fieldDef.optionsLocalized) === null || _a === void 0 ? void 0 : _a[index]) || option; };
    const handleFieldUpdate = (field, value) => {
        if (component) {
            // Only update if value has changed
            const originalValue = originalValuesRef.current[field];
            const hasChanged = originalValue !== value;
            if (hasChanged) {
                // Update the ref to track the new value
                originalValuesRef.current[field] = value;
                debouncedUpdate(component.id, field, value);
            }
        }
    };
    const handleSettingsFieldUpdate = (fieldKey, value) => {
        if (component) {
            // Merge the updated field with existing settings
            const currentDraftSettings = draftSettings || {};
            const updatedSettings = Object.assign(Object.assign({}, currentDraftSettings), { [fieldKey]: value });
            // Only update if value has actually changed
            const originalValue = originalValuesRef.current[`settings.${fieldKey}`];
            const hasChanged = (originalValue !== undefined && originalValue !== value) ||
                (originalValue === undefined && !!(value === null || value === void 0 ? void 0 : value.toString()));
            if (hasChanged) {
                setDraftSettings(updatedSettings);
                // Update the ref to track the new settings object
                originalValuesRef.current[`settings.${fieldKey}`] = value;
                originalValuesRef.current.settings = updatedSettings;
                debouncedSettingsUpdate(component.id, fieldKey, updatedSettings, currentDraftSettings, originalValue);
            }
        }
    };
    const handleToggleActive = () => {
        if (component) {
            void onToggleActive(component.id);
        }
    };
    const renderDynamicField = (fieldDef) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const fieldKey = fieldDef.key;
        const currentValue = (_a = draftSettings[fieldKey]) !== null && _a !== void 0 ? _a : fieldDef.default;
        switch (fieldDef.type) {
            case 'text':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement(InlineEdit, { className: "form-field__value", placeholder: ((_b = fieldDef.default) === null || _b === void 0 ? void 0 : _b.toString()) || fieldDef.placeholder || '--', onBlur: (e) => {
                            const target = e.target;
                            handleSettingsFieldUpdate(fieldKey, target.textContent || '');
                        } }, (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) || '')));
            case 'textarea':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement(InlineEdit, { as: "div", className: "form-field__value form-field__value--textarea", placeholder: ((_c = fieldDef.default) === null || _c === void 0 ? void 0 : _c.toString()) || '--', onBlur: (e) => {
                            const target = e.target;
                            handleSettingsFieldUpdate(fieldKey, target.textContent || '');
                        } }, (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) || '')));
            case 'number':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement(InlineEdit, { variant: "number", className: "form-field__value", numberValue: currentValue, onNumberChange: (value) => handleSettingsFieldUpdate(fieldKey, value), maxLength: getNumberMaxLength(fieldDef.min, fieldDef.max), placeholder: ((_d = fieldDef.default) === null || _d === void 0 ? void 0 : _d.toString()) || fieldDef.placeholder || '0' }, (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) || ''),
                    fieldDef.unit && React.createElement("span", { className: "form-field__unit" },
                        "(",
                        fieldDef.unit,
                        ")")));
            case 'date':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement(InlineEdit, { variant: "date", className: "form-field__value", dateValue: currentValue ? new Date(currentValue) : undefined, onDateChange: (date) => {
                            const next = date ? date.toISOString() : null;
                            handleSettingsFieldUpdate(fieldKey, next);
                        }, placeholder: translate('select_date_placeholder', { namespace }) }, currentValue ? new Date(currentValue).toLocaleDateString() : '')));
            case 'select':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement("div", { className: "form-field__value" },
                        React.createElement(Select, { value: (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) || ((_e = fieldDef.default) === null || _e === void 0 ? void 0 : _e.toString()) || '', options: ((_f = fieldDef.options) === null || _f === void 0 ? void 0 : _f.map((option, index) => ({
                                value: option,
                                label: optionLabel(fieldDef, option, index)
                            }))) || [], onChange: (value) => handleSettingsFieldUpdate(fieldKey, value), showSearch: true, searchPlaceholder: translate('search_field_placeholder', {
                                namespace,
                                field: fieldDef.label.toLowerCase()
                            }) }))));
            case 'multiselect':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement(CheckboxList, { label: fieldDef.label, required: fieldDef.required, options: ((_g = fieldDef.options) === null || _g === void 0 ? void 0 : _g.map((option, index) => ({
                            title: optionLabel(fieldDef, option, index),
                            value: option
                        }))) || [], value: Array.isArray(currentValue) ? currentValue : [], onChange: (toggledValue) => {
                            const currentValues = Array.isArray(currentValue) ? currentValue : [];
                            const newValues = currentValues.includes(toggledValue)
                                ? currentValues.filter((v) => v !== toggledValue)
                                : [...currentValues, toggledValue];
                            handleSettingsFieldUpdate(fieldKey, newValues);
                        } })));
            case 'boolean':
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement(CheckboxList, { label: fieldDef.label, required: fieldDef.required, options: [{ title: fieldDef.label, value: 'true' }], value: currentValue ? ['true'] : [], onChange: () => {
                            const newValue = !currentValue;
                            handleSettingsFieldUpdate(fieldKey, newValue);
                        } })));
            default:
                return (React.createElement("div", { key: fieldKey, className: "form-field" },
                    React.createElement("label", { className: "form-field__label" },
                        renderFieldLabel(fieldDef),
                        ":"),
                    React.createElement("span", { className: "form-field__value" }, (currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) || '--')));
        }
    };
    if (!component)
        return null;
    const modalTitle = (React.createElement("div", { className: "component-detail-modal__header" },
        React.createElement("span", null,
            React.createElement("b", null,
                translate('component', { namespace }),
                ":"),
            " ",
            component.name),
        React.createElement(Button, { className: `component-detail-modal__toggle-active-btn ${component.active ? 'component-detail-modal__toggle-active-btn--deactivate' : ''}`, onClick: handleToggleActive, disabled: isTogglingActive }, isTogglingActive ? (React.createElement("i", { className: "fa fa-spinner fa-spin" })) : (React.createElement(React.Fragment, null,
            React.createElement("i", { className: component.active ? 'fa fa-ban' : 'fa fa-check-circle' }),
            ' ',
            component.active
                ? translate('deactivate', { namespace: 'features.commons' })
                : translate('activate', { namespace: 'features.commons' }))))));
    return (React.createElement(Modal, { title: modalTitle, isOpen: isOpen, onClose: onClose, width: "800px", className: "component-detail-modal" },
        React.createElement("div", { className: "component-detail-form" },
            React.createElement("div", { className: "form-section" },
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, "Parent"),
                    React.createElement("div", { className: "form-field__value--type component-detail-modal__parent-readonly" },
                        React.createElement("span", null, parentDisplay))),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('type_label', { namespace })),
                    React.createElement("div", { className: "form-field__value--type" },
                        React.createElement("span", null, (selectedComponentType === null || selectedComponentType === void 0 ? void 0 : selectedComponentType.typeName) || '--'))),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('name_label', { namespace })),
                    React.createElement(InlineEdit, { className: "form-field__value", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('name', target.textContent || '');
                        } }, component.name || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('location_label', { namespace })),
                    React.createElement(InlineEdit, { className: "form-field__value", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('location', target.textContent || '');
                        } }, component.location || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('description_label', { namespace })),
                    React.createElement(InlineEdit, { as: "div", className: "form-field__value form-field__value--textarea", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('description', target.textContent || '');
                        } }, component.description || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('notes_label', { namespace })),
                    React.createElement(InlineEdit, { as: "div", className: "form-field__value form-field__value--textarea", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('notes', target.textContent || '');
                        } }, component.notes || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('barcode_label', { namespace })),
                    React.createElement(InlineEdit, { className: "form-field__value", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('barcode', target.textContent || '');
                        } }, component.barcode || '')),
                React.createElement("div", { className: "form-field" },
                    React.createElement("label", { className: "form-field__label" }, translate('external_id_label', { namespace })),
                    React.createElement(InlineEdit, { className: "form-field__value", placeholder: "--", onBlur: (e) => {
                            const target = e.target;
                            handleFieldUpdate('externalId', target.textContent || '');
                        } }, component.externalId || ''))),
            fieldDefinitions.length > 0 && (React.createElement("div", { className: "form-section" },
                React.createElement("h3", { className: "form-section__title" }, translate('component_specific_fields_title', { namespace })),
                fieldDefinitions.map(renderDynamicField))))));
}
